import React from "react";
import { FaEye, FaThermometerHalf } from "react-icons/fa";
import {
  categorizePotTeaSettings,
  categorizePotTurkishCoffeeSettings,
  parseLabel,
} from "./constants";
import { getChangedPotSettingsForComponent } from "./utils";
import SettingInput from "./SettingInput";
import { BrewingSettingsMatrix } from "./storageRoles/brewingSettings";
import { useUpdateSettingMutation } from "../../../../store/storageRoles/devicesSharedApi";
import useAuth from "../../../../Hooks/useAuth";
import { useAlert } from "../../../../context/AlertContext";

const PotSettings = ({
  tempSettings,
  deviceSettings,
  selectedDevice,
  updateDeviceSettingIsLoading,
  hasChanges,
  allIndexes,
  activePotIndex,
  setActivePotIndex,
  handlePotSettingChange,
  handleCancelChanges,
  handleSaveChanges,
  isDrawer = false,
  readOnly = false,
}) => {
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const [updateSetting, { isLoading: savingCups }] = useUpdateSettingMutation();

  if (!tempSettings) return null;

  const categorizedTeaPots = categorizePotTeaSettings(
    selectedDevice?.deviceInfo?.deviceName || selectedDevice?.deviceName
  );
  const categorizedPotsTurkishCoffee = categorizePotTurkishCoffeeSettings(
    selectedDevice?.deviceInfo?.deviceName || selectedDevice?.deviceName
  );

  const potCount = tempSettings.pots?.length || 0;

  const serialNo =
    selectedDevice?.serialNo ||
    selectedDevice?.deviceInfo?.serialNo ||
    tempSettings?.serialNo;

  const handleSaveCups = async (potIndex, cups) => {
    try {
      const nextPots = (tempSettings.pots || []).map((p, i) =>
        i === potIndex
          ? {
              ...p,
              turkishCoffee: { ...(p?.turkishCoffee || {}), cups },
            }
          : p
      );
      await updateSetting({
        token: auth?.accessToken,
        serialNo,
        customSettings: { serialNo, pots: nextPots },
      }).unwrap();
      showSuccess?.("Pişirme ayarları kaydedildi");
    } catch (e) {
      showError?.("Cihaz cevap vermedi, tekrar deneyin");
    }
  };

  const findTurkishCoffeePotIndex = () =>
    (tempSettings.pots || []).findIndex(
      (p) => p?.turkishCoffee && Array.isArray(p.turkishCoffee.cups)
    );

  if (readOnly && (!tempSettings.pots || potCount === 0)) {
    return (
      <div className="overflow-y-auto px-3 sm:px-1 mt-2 flex items-center justify-center min-h-[120px] text-gray-500 text-sm">
        Veritabanında bu cihaz için kayıtlı son ayar bulunamadı. Cihaz bir kez bağlandığında ayarlar burada gösterilecektir.
      </div>
    );
  }

  const changedPotSettings = getChangedPotSettingsForComponent(
    deviceSettings,
    tempSettings,
    activePotIndex
  );

  return (
    <>
      {/* Seçili gözün ayarları */}
      <div className="overflow-y-auto px-3 sm:px-1 mt-2">
        <div
          className={`grid grid-cols-1 gap-4 pb-6 ${updateDeviceSettingIsLoading || readOnly ? "opacity-50 pointer-events-none" : ""
            }`}
        >
          {allIndexes
            .filter((badgeIndex) => badgeIndex === activePotIndex)
            .map((badgeIndex) => {
              // Türk Kahvesi kategorileri ve ayarları
              const turkishCoffeeCategories = categorizedPotsTurkishCoffee
                .map((category) => {
                  const settings = category.settings
                    .map((settingKey) => {
                      for (let i = 0; i < potCount; i++) {
                        const setting = tempSettings.pots[i]?.[settingKey];
                        if (setting && setting.index === badgeIndex) {
                          return {
                            ...setting,
                            key: settingKey,
                            label: parseLabel(setting.label),
                          };
                        }
                      }
                      return null;
                    })
                    .filter(Boolean);
                  return settings.length > 0 ? { ...category, settings } : null;
                })
                .filter(Boolean);

              // Çay kategorileri ve ayarları
              const teaCategories = categorizedTeaPots
                .map((category) => {
                  const settings = category.settings
                    .map((settingKey) => {
                      for (let i = 0; i < potCount; i++) {
                        const setting = tempSettings.pots[i]?.[settingKey];
                        if (setting && setting.index === badgeIndex) {
                          return {
                            ...setting,
                            key: settingKey,
                            label: parseLabel(setting.label),
                          };
                        }
                      }
                      return null;
                    })
                    .filter(Boolean);
                  return settings.length > 0 ? { ...category, settings } : null;
                })
                .filter(Boolean);

              if (
                turkishCoffeeCategories.length === 0 &&
                teaCategories.length === 0
              )
                return null;

              const tcPotIndex = findTurkishCoffeePotIndex();
              const tcPot =
                tcPotIndex >= 0 ? tempSettings.pots[tcPotIndex] : null;
              const showMatrix =
                tcPot &&
                Array.isArray(tcPot.turkishCoffee?.cups) &&
                tcPot.turkishCoffee.cups.length > 0;

              return (
                <div key={badgeIndex} className="space-y-4">
                  {showMatrix && (
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-3 sm:p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex h-5 w-5 items-center justify-center rounded-md bg-indigo-100 text-indigo-600">
                          <FaThermometerHalf className="h-3 w-3" />
                        </div>
                        <span className="text-xs font-semibold text-gray-600">
                          Türk Kahvesi Pişirme Ayarları
                        </span>
                      </div>
                      <BrewingSettingsMatrix
                        initialCups={tcPot.turkishCoffee.cups}
                        onSave={(cups) => handleSaveCups(tcPotIndex, cups)}
                        saving={savingCups}
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    {/* Türk Kahvesi kategorileri */}
                    {turkishCoffeeCategories.map((category) => (
                      <div
                        key={category.title}
                        className="overflow-hidden rounded-xl border border-gray-200 bg-white"
                      >
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/60">
                          <div className="flex h-5 w-5 items-center justify-center rounded-md bg-indigo-100 text-indigo-600">
                            <category.icon className="h-3 w-3" />
                          </div>
                          <span className="text-xs font-semibold text-gray-600">
                            {category.title}
                          </span>
                        </div>
                        <div className="p-3 pt-4 sm:p-4 sm:pt-5 min-w-0">
                          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 ${!isDrawer ? "xl:grid-cols-3" : ""}`}>
                            {category.settings.map((setting) => {
                              const settingKey = `${setting.key}_${setting.index}`;
                              return (
                                <SettingInput
                                  key={settingKey}
                                  setting={setting}
                                  onChange={(value) =>
                                    handlePotSettingChange(
                                      setting.index,
                                      setting.key,
                                      value
                                    )
                                  }
                                  isChanged={!!changedPotSettings[settingKey]}
                                  originalValue={
                                    changedPotSettings[settingKey]?.original
                                  }
                                  disabled={readOnly}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                    {/* Çay kategorileri */}
                    {teaCategories.map((category) => (
                      <div
                        key={category.title}
                        className="overflow-hidden rounded-xl border border-gray-200 bg-white"
                      >
                        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/60">
                          <div className="flex h-5 w-5 items-center justify-center rounded-md bg-emerald-100 text-emerald-600">
                            <category.icon className="h-3 w-3" />
                          </div>
                          <span className="text-xs font-semibold text-gray-600">
                            {category.title}
                          </span>
                        </div>
                        <div className="p-3 pt-4 sm:p-4 sm:pt-5 min-w-0">
                          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3 ${!isDrawer ? "xl:grid-cols-3" : ""}`}>
                            {category.settings.map((setting) => {
                              const settingKey = `${setting.key}_${setting.index}`;
                              return (
                                <SettingInput
                                  key={settingKey}
                                  setting={setting}
                                  onChange={(value) =>
                                    handlePotSettingChange(
                                      setting.index,
                                      setting.key,
                                      value
                                    )
                                  }
                                  isChanged={!!changedPotSettings[settingKey]}
                                  originalValue={
                                    changedPotSettings[settingKey]?.original
                                  }
                                  disabled={readOnly}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </>
  );
};

export default PotSettings;
